import { NotifyHelper } from 'classes/helpers/notify.helper';
import env from 'config';
import { isBefore, parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { AuthEvent } from 'enums/auth';
import { CookieKey } from 'enums/cookies.enums';
import { t } from 'i18next';
import {
  IUserRoleFeatureFlags,
  getUserFeatureFlags,
} from 'interfaces/i-user-feature-flags';
import { TeamsHelper } from 'lib_ts/classes/teams.helper';
import { UserRole } from 'lib_ts/enums/auth.enums';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { TrainingMode } from 'lib_ts/enums/machine.enums';
import { Competition } from 'lib_ts/enums/team.enums';
import {
  DEFAULT_SESSION_UNAUTH,
  ILoginError,
  ILoginPayload,
  ILoginResult,
  IResetPasswordPayload,
  ISessionCurrent,
} from 'lib_ts/interfaces/common/i-login';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { SessionMode } from 'lib_ts/interfaces/i-auth-token';
import { ITeam } from 'lib_ts/interfaces/i-team';
import { IUser } from 'lib_ts/interfaces/i-user';
import {
  DEFAULT_REASSIGN_OPTIONS,
  IReassignOptions,
} from 'lib_ts/interfaces/pitches';
import { refreshTokenHandler } from 'services/_https-client';
import { AuthService } from 'services/auth.service';
import { MainService } from 'services/main.service';
import { SessionEventsService } from 'services/session-events.service';
import { TermsService } from 'services/terms.service';
import { useCookiesStore } from 'stores/use-cookies-store';
import useFeatureFlagsStore from 'stores/use-feature-flags-store';
import { create } from 'zustand';

// TODO: split this into multiple slices
type AuthState = {
  // only set once, when the user first logs in/resumes
  sessionStart: Date | undefined;
  current: ISessionCurrent;
  team: ITeam | undefined; // for additional permissions/configurations that depend on how the team is setup

  isInGame: boolean;

  userRoleFeatureFlags: IUserRoleFeatureFlags;

  error: ILoginError | undefined;

  loading: boolean;

  /** teams / machines / other users the user can interact with (e.g. for pitch list reassignment) */
  reassignOptions: IReassignOptions;

  // based on team league and extraLeagues
  leagueTeamOptions: IOption[];

  // ! deprecate this if we have no plans for multiple training modes going forward
  trainingMode: TrainingMode;

  showSafetyPolicy: boolean;
  showTermsOfUse: boolean;

  resumingLogin: boolean;

  wsConnected: boolean;
};

export type AuthActions = {
  setTeam: (value: ITeam | undefined) => void;
  setLoading: (loading: boolean) => void;
  setError: (error?: ILoginError) => void;

  setCurrentSession: (session: Partial<ISessionCurrent>) => void;

  checkTerms: () => void;

  resetPassword: (payload: IResetPasswordPayload) => Promise<boolean>;

  impersonate: (config: { email: string }) => Promise<boolean>;
  newSession: () => Promise<boolean>;

  updateUser: (user: Partial<IUser>, silently?: boolean) => Promise<IUser>;

  rotateTokens: (createNewSessionID?: boolean) => Promise<void>;

  logout: ({
    reason,
    silent,
  }: {
    reason: AuthEvent;
    silent?: boolean;
  }) => Promise<void>;

  loginLegacy: (config: ILoginPayload) => Promise<ILoginResult>;

  checkAppVersion: () => Promise<void>;
  setShowSafetyPolicy: (show: boolean) => void;
  setShowTermsOfUse: (show: boolean) => void;
  setIsInGame: (value: boolean) => void;
  setTrainingMode: (mode: TrainingMode) => Promise<void>;

  updateReassignOptions: (session: ISessionCurrent) => Promise<void>;
  updateLeagueTeamOptions: (
    session: ISessionCurrent,
    isSuperAdmin: boolean
  ) => void;
  updateFeatureFlags: (session: ISessionCurrent) => void;

  toggleSuperSession: () => Promise<boolean>;

  setResumingLogin: (resuming: boolean) => void;

  setWsConnected: (connected: boolean) => void;
};

type AuthStore = AuthState & AuthActions;

/** determine whether the given datestring means the user must accept before proceeding/closing */
export const safetyPolicyAcceptanceRequired = (ds?: string): boolean => {
  if (!ds) {
    /** user has never accepted before */
    return true;
  }

  const lastAccepted = new Date(ds);
  if (isBefore(lastAccepted, new Date(env.safetyPolicyRevisionDate))) {
    /** user has not accepted an updated policy */
    return true;
  }

  return false;
};

const getVersionMatch = (version1: string, version2: string) => {
  const dl = '.';

  const v1Parts = version1.split(dl);
  const v2Parts = version2.split(dl);

  const tiers = ['major', 'minor', 'build', 'patch'];

  const output: { [tier: string]: boolean } = {};

  tiers.forEach((t, i) => {
    output[t] = v1Parts.slice(0, i).join(dl) === v2Parts.slice(0, i).join(dl);
  });

  return output as {
    major: boolean;
    minor: boolean;
    build: boolean;
    patch: boolean;
  };
};

// based on user's league
const getTeamOptions = (
  session: ISessionCurrent,
  isSuperAdmin: boolean
): IOption[] =>
  Object.values(Competition)
    .filter((l) => {
      if (isSuperAdmin) {
        return true;
      }

      if (session.league === l) {
        return true;
      }

      if (session.extraLeagues && session.extraLeagues.includes(l)) {
        return true;
      }

      return false;
    })
    .flatMap((l) => TeamsHelper.getTeamOptions(l, false));

const getDefaultState = (): AuthState => {
  const session = {
    ...DEFAULT_SESSION_UNAUTH,
    // drop in stored auth/email/method
    ...useCookiesStore.getState().authSession,
  };
  const userRoleFeatureFlags = getUserFeatureFlags(session.role, false);

  const isImposter = session.mode === SessionMode.Impostor;
  const isSuperAdmin = isImposter || session.role === UserRole.admin;

  const leagueTeamOptions = getTeamOptions(session, isSuperAdmin);

  return {
    sessionStart: undefined,
    current: session,
    error: undefined,
    userRoleFeatureFlags,
    leagueTeamOptions,
    isInGame: false,
    loading: false,
    reassignOptions: DEFAULT_REASSIGN_OPTIONS,
    trainingMode: TrainingMode.Quick,
    showSafetyPolicy: false,
    showTermsOfUse: false,
    // start the app in a resuming state, and allow useResumeLogin to set it to false when done or not needed
    resumingLogin: true,
    wsConnected: false,
    team: undefined,
  };
};

export const useAuthStore = create<AuthStore>((set, get) => ({
  ...getDefaultState(),

  setWsConnected: (connected) => {
    set({ wsConnected: connected });
  },

  team: undefined,
  setTeam: (v) => set({ team: v }),

  setCurrentSession: async (session) => {
    const {
      current,
      updateReassignOptions,
      updateLeagueTeamOptions,
      updateFeatureFlags,
    } = get();

    const newSession = {
      ...current,
      ...session,
    };

    newSession.auth = !!newSession.token;

    set((prev) => ({
      sessionStart: prev.sessionStart ?? new Date(),
      current: newSession,
    }));

    // * we store the sessionID so we can maintain the same ID when we refresh the token
    if (newSession.auth) {
      sessionStorage.setItem('sessionID', newSession.session);
    } else {
      sessionStorage.removeItem('sessionID');
    }

    await updateReassignOptions(newSession);

    const isImposter = session.mode === SessionMode.Impostor;
    const isSuperAdmin = isImposter || session.role === UserRole.admin;

    updateLeagueTeamOptions(newSession, isSuperAdmin);
    updateFeatureFlags(newSession);

    // runtime (DB-backed) feature flags: fetch the server-evaluated map once on
    // login, clear it when signing out. Distinct from the role-based
    // userRoleFeatureFlags above.
    if (newSession.auth) {
      void useFeatureFlagsStore.getState().fetchFlags();
    } else {
      useFeatureFlagsStore.getState().reset();
    }

    useCookiesStore.getState().setAuthSessionCookie(newSession);

    // todo: deprecate this if we confirm no one needs to name their sessions
    if (newSession.session !== current.session) {
      await SessionEventsService.createOne({
        category: 'auth',
        tags: 'login',
      });
    }
  },

  checkTerms: async () => {
    const result = await TermsService.getInstance().check();

    if (!safetyPolicyAcceptanceRequired(result)) {
      return;
    }

    set({ showSafetyPolicy: true });
  },

  impersonate: async (config) => {
    const { userRoleFeatureFlags, isInGame, setCurrentSession } = get();

    try {
      /** only super admins can impersonate other users */
      if (!userRoleFeatureFlags.impersonation) {
        NotifyHelper.warning({
          message_md:
            'You do not have the necessary permissions to perform this action.',
        });
        return false;
      }

      const FN_NAME = 'ImpersonateUser';

      set({ loading: true });

      const result = await AuthService.getInstance()
        .startImpersonation(config)
        .finally(() => set({ loading: false }));

      if (!result?.success) {
        throw new Error(`${FN_NAME}: empty result from impersonate request`);
      }

      const { success, team } = result;

      if (!success.token) {
        throw new Error(`${FN_NAME}: empty token from impersonate request`);
      }

      if (success.mode !== SessionMode.Impostor) {
        throw new Error(
          `${FN_NAME}: invalid mode ${success.mode} from impersonate request`
        );
      }

      // update team once impersonation has started
      set({ team: team });

      NotifyHelper.success({
        message_md: `Starting a new session impersonating user ${success.email}...`,
      });

      // this will trigger a new socket connection via useSocket hook, no need to manually create a new one
      await setCurrentSession(success);

      const { current } = get();

      // update feature flags to match the impersonated user's role
      set({
        userRoleFeatureFlags: getUserFeatureFlags(current.role, isInGame),
      });

      const { setAuthSessionCookie } = useCookiesStore.getState();
      setAuthSessionCookie(success);

      return true;
    } catch (e) {
      console.error(e);

      NotifyHelper.error({
        message_md: `There was an error impersonating user ${config.email}.`,
      });

      return false;
    }
  },

  newSession: async () => {
    try {
      const { current, setCurrentSession } = get();
      const FN_NAME = 'NewSession';

      if (!current.auth) {
        NotifyHelper.warning({
          message_md: 'You need to be authenticated to perform this action.',
        });
        return false;
      }

      set({ loading: true });

      const success = await AuthService.getInstance().newSession();

      if (!success) {
        throw new Error(`${FN_NAME}: received an empty response`);
      }

      if (!success.token) {
        throw new Error(`${FN_NAME}: received an empty token`);
      }

      // todo: this shouldn't be necessary since useSocket will detect the updated token and createSocket accordingly
      // if (current.session !== success.session) {
      //   console.debug(
      //     `${FN_NAME}: recreating WS connection because session changed`,
      //     success.session
      //   );
      //   WebSocketService.init(success.token);
      // }

      await setCurrentSession(success);

      return true;
    } catch (e) {
      console.error(e);

      return false;
    } finally {
      set({ loading: false });
    }
  },

  loginLegacy: async (config) => {
    const { setCurrentSession, logout } = get();
    try {
      const FN_NAME = 'LoginLegacy';

      set({ loading: true });

      const result = await AuthService.getInstance().loginLegacy(config);

      const { success, team, error } = result;

      if (!success) {
        throw new Error(error?.message ?? `${FN_NAME}: failed login`);
      }

      set({ team: team });

      // todo: this shouldn't be necessary since useSocket will detect the updated token and createSocket accordingly
      // if (current.session !== success.session) {
      //   console.debug(
      //     `${FN_NAME}: recreating WS connection because session changed`,
      //     current.session,
      //     success.session
      //   );
      //   WebSocketService.init(success.token);
      // }

      if (!success) {
        throw new Error('Failed legacy login');
      }

      const { setAuthSessionCookie } = useCookiesStore.getState();
      setAuthSessionCookie(success);

      await setCurrentSession(success);

      return result;
    } catch (e) {
      console.error(e);

      logout({ reason: 'failed-login', silent: true });

      const output: ILoginResult = {
        error: {
          type: 'internal',
          message:
            'There was an unexpected problem authenticating via Legacy Login.',
        },
      };

      return output;
    } finally {
      set({ loading: false });
    }
  },

  resetPassword: async (payload) => {
    set({ loading: true });

    try {
      const result = await AuthService.getInstance().resetPassword(payload);

      if (!result.success) {
        NotifyHelper.warning({
          message_md: result.error ?? t('common.request-failed-msg'),
        });
      }

      return result.success;
    } catch (e) {
      console.error(e);
      NotifyHelper.error({
        message_md: t('common.request-failed-msg'),
      });
      return false;
    } finally {
      set({ loading: false });
    }
  },

  updateUser: async (user, silently) => {
    const { current, setCurrentSession } = get();
    /** ensure this method is never used to update any other user but the current user */
    user._id = current.userID;

    if (!silently) {
      set({ loading: true });
    }

    try {
      const result = await AuthService.getInstance().updateUser(user);

      await setCurrentSession({
        menu_mode: result.menu_mode,
        placeholder_folders: result.placeholder_folders,
        plate_show_ellipses: result.plate_show_ellipses,
        preset_training_mode: result.preset_training_mode,
        disable_auto_videos: result.disable_auto_videos,
        training_mode: result.training_mode,
        language: result.language,
      });

      return result;
    } finally {
      set({ loading: false });
    }
  },

  // we store training_mode both in the session, as well as in it's own state variable to account for game status restrictions
  setTrainingMode: async (mode: TrainingMode) => {
    const { updateUser } = get();
    await updateUser({ training_mode: mode }); // update session
    set({ trainingMode: mode });
  },

  toggleSuperSession: async () => {
    const { userRoleFeatureFlags, setCurrentSession, logout } = get();

    try {
      /** only super admins can enter super mode */
      if (!userRoleFeatureFlags.controls.superMode) {
        NotifyHelper.warning({
          message_md:
            'You do not have the necessary permissions to perform this action.',
        });
        return false;
      }

      set({ loading: true });

      const result = await AuthService.getInstance().toggleSuperMode();

      if (!result?.token) {
        NotifyHelper.warning({
          message_md: t('common.request-failed-msg'),
        });

        throw new Error('failed super session toggle');
      }

      const isSuper = result.mode === 'super';

      NotifyHelper.success({
        message_md: isSuper
          ? 'Starting a super session...'
          : 'Leaving a super session...',
      });

      await setCurrentSession(result);

      return true;
    } catch (e) {
      console.error(e);

      logout({ reason: 'failed-login', silent: true });

      return false;
    } finally {
      set({ loading: false });
    }
  },

  // * Use logout() from useLogout() hook instead of this directly since it handles clearing Auth0 state as well
  logout: async ({ reason, silent }) => {
    const { setCurrentSession } = get();
    const { removeCookie } = useCookiesStore.getState();

    if (!silent) {
      switch (reason) {
        case 'logout': {
          NotifyHelper.info({ message_md: 'Logging you out, goodbye!' });
          break;
        }

        case 'invalid-token': {
          NotifyHelper.warning({
            message_md: 'Invalid token detected, please login again.',
          });
          break;
        }

        case 'auth0-login-migration': {
          NotifyHelper.warning({
            message_md: 'Your session has expired, please login again.',
          });
          break;
        }

        case 'server-offline': {
          NotifyHelper.warning({
            message_md: 'Server could not be reached, please try again later.',
          });
          break;
        }

        default: {
          /** most likely a failed login, notify would be handled in auth service */
          break;
        }
      }
    }

    await setCurrentSession(DEFAULT_SESSION_UNAUTH);

    removeCookie(CookieKey.snapshot);
    removeCookie(CookieKey.auth);
    removeCookie(CookieKey.refresh);
    removeCookie(CookieKey.authSession);

    await AuthService.getInstance().logout();
  },

  rotateTokens: async (createNewSessionID) => {
    // onRefreshSuccess sets team + current session; onRefreshFail logs out
    try {
      const result =
        await refreshTokenHandler.refreshAuthToken(createNewSessionID);

      if (!result?.success) {
        throw new Error('Unable to resume login session');
      }
    } catch {
      // refreshAuthToken already invoked onRefreshFail
    }
  },

  setLoading: (loading) => set({ loading }),
  setError: (error?) => set({ error }),

  checkAppVersion: async () => {
    const { logout } = get();

    try {
      if (env.maintenance) {
        return;
      }

      /** ensures server is online */
      const result = await MainService.getInstance().check();

      if (!result) {
        /** auto-logout, e.g. server is down for maintenance or the user's cookies are invalid */
        logout({
          reason: 'server-offline',
        });
        return;
      }

      const versMatch = getVersionMatch(result.version, env.version);

      // no problem when build version still matches
      if (versMatch.build) {
        return;
      }

      if (typeof result.updated === 'string') {
        result.updated = parseISO(result.updated);
      }

      NotifyHelper.warning({
        message_md: [
          `The application has been updated to **v${
            result.version
          }** on ${format(result.updated, 'yyyy-MM-dd @ h:mm a', {
            timeZone: LOCAL_TIMEZONE,
          })}.`,
          'Please refresh this page using the button below or using your browser when you are ready.',
        ].join('\n\n'),
        delay_ms: 0,
        inbox: true,
        buttons: [
          {
            label: t('common.refresh'),
            onClick: () =>
              logout({
                reason: 'logout',
              }),
            dismissAfterClick: true,
          },
        ],
      });
    } catch (e) {
      console.error(e);

      logout({
        reason: 'failed-check',
      });
    }
  },

  setShowSafetyPolicy: (show) => set({ showSafetyPolicy: show }),
  setShowTermsOfUse: (show) => set({ showTermsOfUse: show }),

  setIsInGame: (value) => {
    const { isInGame: prevValue } = get();

    // only notify the user if entering into game
    if (!prevValue && value) {
      NotifyHelper.warning({
        message_md: 'A game is underway, entering offline mode.',
      });
    }

    set({ isInGame: value });
  },

  updateReassignOptions: async (session) => {
    if (!session.auth || !session.token) {
      /** default behaviour if not authorized */
      set({
        reassignOptions: DEFAULT_REASSIGN_OPTIONS,
      });
      return;
    }

    const newReassignOptions =
      await AuthService.getInstance().reassignOptions();

    set({
      reassignOptions: newReassignOptions,
    });
  },

  updateLeagueTeamOptions: async (session, isSuperAdmin) => {
    const newLeagueTeamOptions = getTeamOptions(session, isSuperAdmin);
    set({ leagueTeamOptions: newLeagueTeamOptions });
  },

  updateFeatureFlags: (session) => {
    const { isInGame } = get();
    const newFeatureFlags = getUserFeatureFlags(session.role, isInGame);
    set({ userRoleFeatureFlags: newFeatureFlags });
  },

  setResumingLogin: (resuming) => set({ resumingLogin: resuming }),
}));
